// 🌐 SIMPLIFIED HTTP CLIENT
// ✅ Automatic retry logic with exponential backoff
// ✅ Timeout handling with AbortController
// ✅ Basic error handling for 401s

export interface HttpClientConfig {
  baseUrl?: string;
  timeout?: number;
  retries?: number;
}

export class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

class HttpClient {
  private baseUrl: string;
  private timeout: number;
  private retries: number;

  constructor(config: HttpClientConfig = {}) {
    this.baseUrl = config.baseUrl || import.meta.env.VITE_API_URL || "/api";
    this.timeout = config.timeout || 10000;
    this.retries = config.retries || 2;
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    // For now, no authentication token - can be added later
    const token: string | null = null;

    // Only force application/json when the body is a string-ish JSON payload.
    // For FormData uploads we MUST let the browser set Content-Type itself so
    // it can include the multipart boundary parameter — otherwise the
    // server-side multipart parser rejects the body with a 400 and the
    // upload silently fails (this broke vocab image uploads). Blob/URLSearch-
    // Params get the same treatment.
    const isMultipart =
      typeof FormData !== "undefined" && options.body instanceof FormData;
    const isBlob = typeof Blob !== "undefined" && options.body instanceof Blob;
    const isUrlEncoded =
      typeof URLSearchParams !== "undefined" &&
      options.body instanceof URLSearchParams;
    const shouldSetJsonContentType =
      Boolean(options.body) && !isMultipart && !isBlob && !isUrlEncoded;

    const requestOptions: RequestInit = {
      ...options,
      headers: {
        ...(options.headers || {}),
        ...(shouldSetJsonContentType
          ? { "Content-Type": "application/json" }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    let lastError: Error;

    // Retry logic
    for (let attempt = 0; attempt <= this.retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        const response = await fetch(url, {
          ...requestOptions,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // Handle authentication errors
        if (response.status === 401) {
          console.warn("401 Unauthorized - Authentication required");
          throw new Error("Authentication required");
        }

        if (!response.ok) {
          // Try to read the response body so the caller sees the real
          // validation/error message (Fastify validation errors carry
          // a `message` like "body must have required property 'X'").
          let body: unknown = undefined;
          let bodyMessage: string | undefined;
          try {
            const ct = response.headers.get("content-type") || "";
            if (ct.includes("application/json")) {
              body = await response.json();
              if (body && typeof body === "object") {
                const b = body as Record<string, unknown>;
                bodyMessage =
                  (typeof b.message === "string" && b.message) ||
                  (typeof b.error === "string" && b.error) ||
                  undefined;
              }
            } else {
              const text = await response.text();
              if (text) {
                body = text;
                bodyMessage = text.slice(0, 500);
              }
            }
          } catch {
            // best-effort; fall through to generic message
          }
          const message = bodyMessage
            ? `HTTP ${response.status}: ${bodyMessage}`
            : `HTTP ${response.status}: ${response.statusText}`;
          throw new HttpError(response.status, message, body);
        }

        // Handle responses with no content (like DELETE operations)
        if (
          response.status === 204 ||
          response.headers.get("content-length") === "0"
        ) {
          return undefined as T;
        }

        // Check if response has content to parse
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          return data;
        }

        // For non-JSON responses, return undefined
        return undefined as T;
      } catch (error) {
        lastError =
          error instanceof Error ? error : new Error("Request failed");

        // Don't retry on auth errors or client errors (4xx)
        if (error instanceof Error) {
          if (
            error.message.includes("401") ||
            error.message.includes("Authentication") ||
            error.message.includes("HTTP 4")
          ) {
            throw error;
          }
        }

        // Wait before retry (exponential backoff)
        if (attempt < this.retries) {
          await new Promise((resolve) =>
            setTimeout(resolve, Math.pow(2, attempt) * 1000),
          );
        }
      }
    }

    throw lastError!;
  }

  // Convenience methods
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const httpClient = new HttpClient();
